import React, { ChangeEvent, useEffect, useMemo, useState, useRef } from "react";
import RuleFieldMapper from "./component/RuleFieldMapper";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  Upload,
  ChevronRight,
  FileSpreadsheet,
  MoreVertical,
  AlertTriangle,
  DollarSign,
  Building2,
  Users,
  Loader2,
} from "lucide-react";
import {
  uploadCSV,
  getUploadImports,
  deleteImport,
  startImport,
  getImportCSVData,
} from "../api/AttachmentApi";
import { formatDistanceToNow } from "date-fns";
import LayoutAction from "../actions/LayoutAction";
import { useDispatch, useSelector } from "react-redux";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { updateCurrentUserOnboardingSteps } from "../api/UserApi";
import UserAction from "../actions/UserAction";
import { useQuery } from "../AppLayout";
import { RuleForm } from "./component/RuleForm";
import Tab from "../main/component/Tab";
import { useTranslation } from "react-i18next";
import ProjectAction from "../actions/ProjectAction";
import {
  parseCSVFile,
  ParsedCSV,
  getSampleRows,
  validateCSVStructure,
  parseCSVText,
} from "./utils/csvParser";
import {
  generatePreviewRecords,
  markDuplicatesInPreview,
  PreviewRecord,
} from "./utils/importPreview";
import {
  objectTypesToSelectedEntityTypes,
  inferImportEntityTypeFromMappings,
  getObjectTypeKindFromItem,
  validateImportMappingWithSelectedTypes,
  type SelectedEntityTypes,
  type EntityTypeOption,
} from "./utils/importSettingUtils";
import { validateRow, ValidationError } from "./utils/importValidation";
import PreviewTable from "./components/PreviewTable";
import DuplicateWarning from "./components/DuplicateWarning";
import ReviewValues from "./components/ReviewValues";
import ImportListSection from "./components/ImportListSection";
import ChartLoading from "../insight/components/ChartLoading";

// --- Constants ---
const ImportSettingTabs = {
  SETTING: {
    key: "SETTING",
    value: <span className={"px-1.5"}>Settings</span>,
  },
  WORKFLOW: {
    key: "WORKFLOW",
    value: <span className={"px-1.5"}>Workflow</span>,
  },
};

const FILE_SIZE_LIMIT_BYTES = 10 * 1024 * 1024;
const MAX_PREVIEW_SAMPLE_ROWS = 20;

const OBJECT_TYPE_BADGE_PILL = "bg-gray-100 text-gray-700 border border-gray-200";

const ImportSetting = ({ projectId, ruleId, appInfo }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const query = useQuery();
  const onboarding = query.get("onboarding") || false;
  const importTypeFromUrl = query.get("type");

  // --- State: step flow ---
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [parsedCSV, setParsedCSV] = useState<ParsedCSV | null>(null);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [previewContacts, setPreviewContacts] = useState<PreviewRecord[]>([]);
  const [previewCompanies, setPreviewCompanies] = useState<PreviewRecord[]>([]);
  const [previewDeals, setPreviewDeals] = useState<PreviewRecord[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [duplicateStrategy, setDuplicateStrategy] = useState<"skip" | "update">("update");
  const [activePreviewTab, setActivePreviewTab] = useState<"contacts" | "companies" | "deals">(
    "contacts"
  );

  // Import types: multi-select — Contacts, Accounts, Deals (separate options)
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<SelectedEntityTypes>(() =>
    importTypeFromUrl === "deal" ? ["SALE"] : ["CONTACT", "COMPANY"]
  );

  // --- State: mapping & CSV ---
  const [csvFields, setCsvFields] = useState([]);
  const [mappings, setMappings] = useState({});
  /** Validation errors for mapping step (Step 3); displayed in banner. Array of strings. */
  const [errorMessage, setErrorMessage] = useState([]);

  // --- State: import list ---
  const [imports, setImports] = useState([]);
  const [importsListLoading, setImportsListLoading] = useState(false);
  const [loadingImportDetail, setLoadingImportDetail] = useState(false);
  const [currentImport, setCurrentImport] = useState<any>({ headers: "" });
  const [showUpload, setShowUpload] = useState(false);
  const [importSearchQuery, setImportSearchQuery] = useState("");
  const [importStatusFilter, setImportStatusFilter] = useState<
    "" | "done" | "draft" | "failed" | "processing"
  >("");

  // --- State: UI & dialogs ---
  const [activeTab, setActiveTab] = useState("SETTING");
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [showImportResult, setShowImportResult] = useState(false);
  const [importResult, setImportResult] = useState<{
    successCount: number;
    failCount: number;
    message?: string;
    detailedErrors?: string;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; importId?: string }>({
    show: false,
  });
  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});

  // --- Refs ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fetchedDraftRef = useRef<string | null>(null);

  // --- Derived / selectors ---
  const projectFields = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "fields"]) || []
  );

  const importSteps = useMemo(
    () => [
      { number: 1, label: t("File info"), key: "fileinfo" },
      { number: 2, label: t("Upload CSV"), key: "upload" },
      { number: 3, label: t("Map Columns"), key: "map" },
      { number: 4, label: t("Review Values"), key: "review" },
      { number: 5, label: t("Preview Import"), key: "preview" },
    ],
    [t]
  );

  // --- Helpers used by effects (must be declared before effects that call them) ---
  const applyImportApiDataToState = (data: any) => {
    if (!data || Array.isArray(data) || typeof data === "string") return;
    if (data.mappings && typeof data.mappings === "object" && data.status !== "draft") {
      setMappings(data.mappings);
    }
    if (data.objectTypes != null) {
      setSelectedEntityTypes(objectTypesToSelectedEntityTypes(data.objectTypes));
    } else if (data.mappings && typeof data.mappings === "object") {
      const inferred = inferImportEntityTypeFromMappings(data.mappings);
      setSelectedEntityTypes(inferred === "deal" ? ["SALE"] : ["CONTACT", "COMPANY"]);
    }
    if (data.csvText) {
      const parsed = parseCSVText(data.csvText);
      setCsvRows(getSampleRows(parsed, MAX_PREVIEW_SAMPLE_ROWS));
      setParsedCSV(parsed);
    } else if (data.rows && Array.isArray(data.rows)) {
      setCsvRows(data.rows.slice(0, MAX_PREVIEW_SAMPLE_ROWS));
    }
  };

  const notifyReuploadDraft = () => {
    dispatch(
      LayoutAction.setNotification({
        title: t("Import CSV"),
        type: "warning",
        message: t("Cannot load CSV data for this draft. Please re-upload the file."),
      })
    );
  };

  // Validate records for one entity type and collect errors into allErrors
  const validateRecordsForEntity = <T extends { rowIndex: number }>(
    records: T[],
    entityType: "CONTACT" | "COMPANY" | "SALE",
    allErrors: ValidationError[]
  ): (T & { validationErrors: any[] })[] =>
    records.map((record) => {
      const validation = validateRow(
        csvRows[record.rowIndex],
        record.rowIndex,
        mappings,
        entityType
      );
      validation.errors.forEach((err) =>
        allErrors.push({
          rowIndex: record.rowIndex,
          field: err.field,
          message: err.message,
        })
      );
      return { ...record, validationErrors: validation.errors };
    });

  // Generate preview records for entity types selected in Step 1 (File info)
  const generatePreview = async () => {
    if (csvRows.length === 0 || Object.keys(mappings).length === 0) return;

    const { contacts, companies, deals } = generatePreviewRecords(csvRows, mappings);
    const allErrors: ValidationError[] = [];

    let nextContacts: PreviewRecord[] = [];
    let nextCompanies: PreviewRecord[] = [];
    let nextDeals: PreviewRecord[] = [];

    if (selectedEntityTypes.includes("CONTACT")) {
      const validatedContacts = validateRecordsForEntity(contacts, "CONTACT", allErrors);
      nextContacts = markDuplicatesInPreview(validatedContacts);
    }
    if (selectedEntityTypes.includes("COMPANY")) {
      const validatedCompanies = validateRecordsForEntity(companies, "COMPANY", allErrors);
      nextCompanies = markDuplicatesInPreview(validatedCompanies);
    }
    if (selectedEntityTypes.includes("SALE")) {
      const validatedDeals = validateRecordsForEntity(deals, "SALE", allErrors);
      nextDeals = markDuplicatesInPreview(validatedDeals);
    }

    setPreviewContacts(nextContacts);
    setPreviewCompanies(nextCompanies);
    setPreviewDeals(nextDeals);

    const firstWithData = selectedEntityTypes.find((t) =>
      t === "CONTACT"
        ? nextContacts.length > 0
        : t === "COMPANY"
          ? nextCompanies.length > 0
          : nextDeals.length > 0
    );
    setActivePreviewTab(
      firstWithData === "SALE" ? "deals" : firstWithData === "COMPANY" ? "companies" : "contacts"
    );

    setValidationErrors(allErrors);
  };

  // --- Effects ---
  useEffect(() => {
    const fieldsCount =
      typeof projectFields.size === "number" ? projectFields.size : projectFields.length || 0;
    if (fieldsCount === 0 && projectId) {
      dispatch(ProjectAction.getProject(projectId));
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadList();
  }, []);

  // Auto-poll when any import has status "processing"
  useEffect(() => {
    const hasProcessing = imports.some((item: any) => item.status === "processing");
    if (!hasProcessing) return;

    const intervalId = setInterval(() => {
      getUploadImports().then(setImports);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [imports]);

  useEffect(() => {
    if (appInfo.connected) {
      setErrorMessage([]);
    }
  }, [appInfo]);

  // Sync import types from URL (e.g. when opening Import from Deal view with type=deal)
  useEffect(() => {
    if (importTypeFromUrl === "deal") {
      setSelectedEntityTypes(["SALE"]);
    } else if (importTypeFromUrl === "contact_account" || importTypeFromUrl === "contact") {
      setSelectedEntityTypes(["CONTACT", "COMPANY"]);
    }
  }, [importTypeFromUrl]);

  // When opening with type=deal (or type=contact_account), open at Step 1 "File info" with type pre-selected
  useEffect(() => {
    if (importTypeFromUrl && appInfo.connected) {
      setShowUpload(true);
      setCurrentStep(1);
    }
  }, [importTypeFromUrl, appInfo.connected]);

  useEffect(() => {
    if (parsedCSV && parsedCSV.headers.length > 0) {
      const fields = parsedCSV.headers.map((header) => ({
        label: header.trim(),
        value: header.trim(),
      }));
      setCsvFields(fields);
    } else if (currentImport.headers && currentImport.headers != "") {
      const fields = currentImport.headers
        .split(",")
        .map((header) => ({ label: header.trim(), value: header.trim() }));
      setCsvFields(fields);
      const listMappings = currentImport.mappings || {};
      setMappings(listMappings);
      if (currentImport.objectTypes != null) {
        setSelectedEntityTypes(objectTypesToSelectedEntityTypes(currentImport.objectTypes));
      } else {
        const inferred = inferImportEntityTypeFromMappings(listMappings);
        setSelectedEntityTypes(inferred === "deal" ? ["SALE"] : ["CONTACT", "COMPANY"]);
      }
      setShowUpload(true);
      setErrorMessage([]);
    } else {
      setCsvFields([]);
    }
  }, [currentImport, parsedCSV]);

  useEffect(() => {
    const needToFetchForCurrentImport =
      currentImport.importId &&
      (currentImport.status === "draft" || currentImport.status === "done") &&
      fetchedDraftRef.current !== currentImport.importId;

    if (parsedCSV && parsedCSV.rows.length > 0 && !needToFetchForCurrentImport) {
      setCsvRows(getSampleRows(parsedCSV, MAX_PREVIEW_SAMPLE_ROWS));
      return;
    }
    if (!needToFetchForCurrentImport) {
      if (parsedCSV?.rows?.length) fetchedDraftRef.current = null;
      return;
    }

    fetchedDraftRef.current = currentImport.importId;
    setLoadingImportDetail(true);

    const onFetchError = (resetRef = false) => {
      if (currentImport.status === "draft" && currentImport.headers && currentImport.headers !== "")
        notifyReuploadDraft();
      setCsvRows([]);
      if (resetRef) fetchedDraftRef.current = null;
    };

    getImportCSVData(currentImport.importId)
      .then(applyImportApiDataToState)
      .catch((err) => {
        console.error("Error fetching CSV data:", err);
        onFetchError(true);
      })
      .finally(() => setLoadingImportDetail(false));
  }, [parsedCSV, currentImport.importId, currentImport.status, currentImport.fileName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (csvRows.length > 0 && Object.keys(mappings).length > 0 && csvFields.length > 0) {
      generatePreview();
    }
  }, [csvRows, mappings, csvFields, selectedEntityTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear validation error when mappings become valid (e.g. user maps required field)
  useEffect(() => {
    if (errorMessage.length === 0) return;
    const result = validateImportMapping();
    if (!result.hasError) {
      setErrorMessage([]);
    }
  }, [mappings, selectedEntityTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Handlers ---
  const loadList = () => {
    setImportsListLoading(true);
    getUploadImports()
      .then(setImports)
      .finally(() => setImportsListLoading(false));
  };

  const handleImportCustomersOnboarding = () => {
    if (onboarding) {
      updateCurrentUserOnboardingSteps("import-customers").then(() => {
        dispatch(UserAction.getCurrentUser());
      });
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > FILE_SIZE_LIMIT_BYTES) {
        setErrorMessage([t("File size exceeds 10MB limit")]);
        return;
      }

      if (!file.name.toLowerCase().endsWith(".csv")) {
        setErrorMessage([t("Please upload a CSV file")]);
        return;
      }

      setUploading(true);
      setErrorMessage([]);
      setParsedCSV(null);
      setCsvRows([]);
      fetchedDraftRef.current = null; // Reset fetch tracking when uploading new file

      try {
        // Parse CSV on frontend first
        const parsed = await parseCSVFile(file);
        const validation = validateCSVStructure(parsed);

        if (!validation.valid) {
          setErrorMessage(validation.errors);
          setUploading(false);
          return;
        }

        setParsedCSV(parsed);

        // Upload to backend with objectTypes (comma-separated: CONTACT, COMPANY, SALE)
        const objectTypes = selectedEntityTypes.join(",");
        const uploadData = await uploadCSV({
          file,
          importId: currentImport.importId,
          objectTypes,
        });

        setCurrentImport({
          ...uploadData,
          headers: parsed.headers.join(","),
        });

        loadList();

        dispatch(
          LayoutAction.setNotification({
            title: t("Import Upload"),
            type: "success",
            message: t("CSV file is uploaded"),
          })
        );
        handleImportCustomersOnboarding();
      } catch (error: any) {
        console.error("Error parsing/uploading CSV:", error);
        setCurrentImport({ headers: "" });
        setParsedCSV(null);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          t("Failed to parse or upload CSV file");
        setErrorMessage(Array.isArray(msg) ? msg : [String(msg)]);
        dispatch(
          LayoutAction.setNotification({
            title: t("Import Upload"),
            type: "error",
            message: msg,
          })
        );
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDelete = (importId: string) => {
    setDeletingIds((s) => ({ ...s, [importId]: true }));
    return deleteImport(importId)
      .then(() => {
        loadList();
        dispatch(
          LayoutAction.setNotification({
            title: t("Import"),
            type: "success",
            message: t("Removed import"),
          })
        );
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Import"),
            type: "error",
            message: t("Failed to remove import"),
          })
        );
      })
      .finally(() => {
        setDeletingIds((s) => ({ ...s, [importId]: false }));
      });
  };

  // --- Validation (delegates to util for testability) ---
  const validateImportMapping = () =>
    validateImportMappingWithSelectedTypes(mappings, selectedEntityTypes, t);

  // --- Step & import handlers ---
  const handleNextStep = () => {
    if (currentStep === 1) {
      // Step 1: File info - go to Upload
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2: Upload CSV - already validated in handleFileChange
      if (parsedCSV && parsedCSV.headers.length > 0) {
        setCurrentStep(3);
      }
    } else if (currentStep === 3) {
      const validateData = validateImportMapping();
      if (!validateData.hasError) {
        setCurrentStep(4);
      } else {
        setErrorMessage(validateData.errors);
      }
    } else if (currentStep === 4) {
      // Step 4: Review Values - move to preview
      setCurrentStep(5);
    } else if (currentStep === 5) {
      // Step 5: Preview Import - start import directly
      const validateData = validateImportMapping();
      if (!validateData.hasError) {
        setShowImportConfirmation(true);
      } else {
        setErrorMessage(validateData.errors);
      }
    }
  };

  const handleStartImportClick = () => {
    const validateData = validateImportMapping();
    if (!validateData.hasError) {
      setShowImportConfirmation(true);
    } else {
      setErrorMessage(validateData.errors);
    }
  };

  const handleCloseImportResult = () => {
    setShowImportResult(false);
    setImportResult(null);
  };

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as 1 | 2 | 3 | 4 | 5);
      setErrorMessage([]);
    }
  };

  const handleResetImport = () => {
    setCurrentStep(1);
    setParsedCSV(null);
    setCsvRows([]);
    setCsvFields([]);
    setMappings({});
    setSelectedEntityTypes(["CONTACT", "COMPANY"]);
    setPreviewContacts([]);
    setPreviewCompanies([]);
    setPreviewDeals([]);
    setValidationErrors([]);
    setShowUpload(false);
    setCurrentImport({});
    setErrorMessage([]);
  };

  // Helpers for handleStartImport: parse response and show notifications (kept before handleStartImport for readability)
  const parseImportResponse = (response: any) => {
    const result = response?.result || response;
    return {
      successCount: result?.successCount || result?.success || 0,
      failCount: result?.failCount || result?.fail || 0,
      statusMessage: result?.statusMessage || result?.message,
      detailedErrors: result?.detailedErrors || result?.errors,
    };
  };

  const checkImportStatusAfterDelay = (importId: string) => {
    setTimeout(async () => {
      try {
        const updatedImports = await getUploadImports();
        const updatedImport = updatedImports.find((imp: any) => imp.importId === importId);
        const message = updatedImport?.statusMessage;
        const detailedErrors = updatedImport?.detailedErrors;
        if (!message && !detailedErrors) return;
        const textToParse = message || detailedErrors || "";
        const successMatch = textToParse.match(/(\d+)\s+successful/);
        const failMatch = textToParse.match(/(\d+)\s+fail/);
        const parsedSuccessCount = successMatch ? parseInt(successMatch[1]) : 0;
        const parsedFailCount = failMatch ? parseInt(failMatch[1]) : 0;
        if (parsedFailCount > 0 || textToParse.includes("fail")) {
          setImportResult({
            successCount: parsedSuccessCount,
            failCount: parsedFailCount,
            message,
            detailedErrors,
          });
          setShowImportResult(true);
        }
      } catch (error) {
        console.error("Error checking import status:", error);
      }
    }, 2000);
  };

  const showImportResultNotification = (
    successCount: number,
    failCount: number,
    statusMessage?: string,
    detailedErrors?: string
  ) => {
    if (failCount > 0 || statusMessage || detailedErrors) {
      setImportResult({
        successCount,
        failCount,
        message: statusMessage,
        detailedErrors,
      });
      setShowImportResult(true);
    }
  };

  const showCompletionNotification = (successCount: number, failCount: number) => {
    if (failCount > 0) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Import completed"),
          type: "warning",
          message: t("Import completed with {success} successful and {fail} fail", {
            success: successCount,
            fail: failCount,
          }),
        })
      );
    } else {
      dispatch(
        LayoutAction.setNotification({
          title: t("Import"),
          type: "success",
          message: t("Import started"),
        })
      );
    }
  };

  const handleStartImport = () => {
    const payloadMappings: Record<string, unknown[]> = {};
    const map = mappings as Record<string, unknown[]>;
    if (selectedEntityTypes.includes("CONTACT")) payloadMappings.CONTACT = map.CONTACT || [];
    if (selectedEntityTypes.includes("COMPANY")) payloadMappings.COMPANY = map.COMPANY || [];
    if (selectedEntityTypes.includes("SALE")) payloadMappings.SALE = map.SALE || [];
    const data = { mappings: payloadMappings, duplicateStrategy };
    setStarting(true);
    setShowImportConfirmation(false);

    startImport(currentImport.importId, data)
      .then((response) => {
        const { successCount, failCount, statusMessage, detailedErrors } =
          parseImportResponse(response);
        showImportResultNotification(successCount, failCount, statusMessage, detailedErrors);
        if (!(failCount > 0 || statusMessage || detailedErrors)) {
          checkImportStatusAfterDelay(currentImport.importId);
        }
        handleResetImport();
        loadList();
        showCompletionNotification(successCount, failCount);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || t("Failed to start import");
        setErrorMessage([message]);
        dispatch(
          LayoutAction.setNotification({
            title: t("Import"),
            type: "error",
            message,
          })
        );
      })
      .finally(() => setStarting(false));
  };

  const renderStepNavigation = () => {
    if (!showUpload || currentImport.status === "done") return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2">
          {importSteps.map((step, index) => {
            const isActive = currentStep === step.number;
            const isClickable = currentStep > step.number;

            return (
              <React.Fragment key={step.key}>
                <button
                  type="button"
                  onClick={() => isClickable && setCurrentStep(step.number as 1 | 2 | 3 | 4 | 5)}
                  disabled={!isClickable}
                  className={`flex items-center gap-2 shrink-0 ${
                    isClickable ? "cursor-pointer" : "cursor-not-allowed"
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                      isActive ? "border-blue-600 bg-blue-50" : "border-gray-300 bg-white"
                    }`}
                  >
                    <span
                      className={`text-sm font-medium ${
                        isActive ? "text-gray-800" : "text-gray-500"
                      }`}
                    >
                      {step.number}
                    </span>
                  </div>
                  <span
                    className={`text-sm font-medium whitespace-nowrap ${
                      isActive ? "text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {step.label}
                  </span>
                </button>
                {index < importSteps.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const handleShowNewImport = () => {
    handleResetImport();
    setShowUpload(true);
    if (!appInfo.connected) {
      setErrorMessage([t("You need to enable this import csv.")]);
    } else {
      setErrorMessage([]);
    }
  };

  const toggleEntityType = (entityType: EntityTypeOption) => {
    const isRemoving = selectedEntityTypes.includes(entityType);
    setSelectedEntityTypes((prev) => {
      const next = isRemoving ? prev.filter((t) => t !== entityType) : [...prev, entityType];
      if (next.length === 0) return prev;
      return next;
    });
    if (isRemoving) {
      const key = entityType === "SALE" ? "SALE" : entityType === "CONTACT" ? "CONTACT" : "COMPANY";
      setMappings((prev) => ({ ...prev, [key]: [] }));
    }
  };

  // --- Render helpers ---
  const renderValidationBanner = () => {
    if (errorMessage.length === 0) return null;
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-red-800"
        role="alert"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-red-600" aria-hidden />
        <div className="space-y-0.5 text-sm">
          {errorMessage.length === 1 ? (
            <p>{errorMessage[0]}</p>
          ) : (
            <ul className="list-disc list-inside space-y-0.5">
              {errorMessage.slice(0, 3).map((msg, i) => (
                <li key={`err_${i}`}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // --- Render: steps 1–5 ---
  const renderStep1FileInfo = () => {
    if (currentStep !== 1) return null;

    const isContactSelected = selectedEntityTypes.includes("CONTACT");
    const isCompanySelected = selectedEntityTypes.includes("COMPANY");
    const isSaleSelected = selectedEntityTypes.includes("SALE");

    return (
      <div className="grid gap-8 md:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("Select what you'd like to import")}
          </h2>
          <p className="text-sm text-gray-600">
            {t(
              "Select one or more options below. You can import Contacts, Accounts, and Deals from the same file."
            )}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => toggleEntityType("CONTACT")}
              className={`flex items-start gap-4 rounded-xl border-2 p-4 text-left transition-colors ${
                isContactSelected
                  ? "border-blue-600 bg-blue-50/50"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
              }`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">{t("Contacts")}</div>
                <div className="mt-1 text-sm text-gray-600">
                  {t("People you communicate with (name, email, etc.).")}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => toggleEntityType("COMPANY")}
              className={`flex items-start gap-4 rounded-xl border-2 p-4 text-left transition-colors ${
                isCompanySelected
                  ? "border-blue-600 bg-blue-50/50"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
              }`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                <Building2 className="h-5 w-5 text-emerald-600" aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">{t("Accounts")}</div>
                <div className="mt-1 text-sm text-gray-600">
                  {t("Organizations or companies (company name, domain, etc.).")}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => toggleEntityType("SALE")}
              className={`flex items-start gap-4 rounded-xl border-2 p-4 text-left transition-colors ${
                isSaleSelected
                  ? "border-blue-600 bg-blue-50/50"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50"
              }`}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <DollarSign className="h-5 w-5 text-amber-600" aria-hidden />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900">{t("Deals")}</div>
                <div className="mt-1 text-sm text-gray-600">
                  {t("Revenue opportunities connected to a company.")}
                </div>
              </div>
            </button>
          </div>
        </div>
        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
          <h3 className="text-sm font-semibold text-gray-900">{t("Import guide")}</h3>
          <p className="text-xs text-gray-600">{t("Mandatory fields for each selected type:")}</p>
          <ul className="list-inside list-disc space-y-1 text-xs text-gray-700">
            {isContactSelected && (
              <>
                <li>
                  {t("Contacts")}: {t("name")} *, {t("email")} *
                </li>
              </>
            )}
            {isCompanySelected && (
              <li>
                {t("Accounts")}: {t("Account name")} *
              </li>
            )}
            {isSaleSelected && (
              <li>
                {t("Deals")}: {t("Deal name")} *
              </li>
            )}
            {!isContactSelected && !isCompanySelected && !isSaleSelected && (
              <li>{t("Select at least one type above.")}</li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const renderStep2Upload = () => {
    if (currentStep !== 2) return null;

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const fakeEvent = {
          target: { files: [file] },
        } as unknown as ChangeEvent<HTMLInputElement>;
        handleFileChange(fakeEvent);
      }
    };

    return (
      <div className="space-y-4">
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`flex justify-center rounded-lg border-2 border-dashed px-6 py-12 transition-colors ${
            uploading
              ? "border-gray-300 bg-gray-50"
              : "border-gray-300 bg-white hover:border-blue-400"
          }`}
        >
          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4 flex items-center justify-center text-sm leading-6 text-gray-600">
              <label
                htmlFor="file-upload-step1"
                className={`relative rounded-md bg-white font-semibold ${
                  uploading
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-blue-600 hover:text-blue-500 cursor-pointer"
                } focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2`}
              >
                <span>{uploading ? t("Uploading...") : t("Upload a file")}</span>
                <input
                  ref={fileInputRef}
                  id="file-upload-step1"
                  name="file-upload-step1"
                  type="file"
                  accept=".csv"
                  className="sr-only"
                  disabled={uploading}
                  onChange={handleFileChange}
                />
              </label>
              {!uploading && <span className="ml-2">{t("or drag and drop")}</span>}
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-600">
              {t("CSV up to 10MB (15k records)")}
            </p>
          </div>
        </div>

        {parsedCSV && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">{t("File name")}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {currentImport.fileName || "uploaded.csv"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{t("Size")}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {currentImport.fileSize ? `${Math.round(currentImport.fileSize / 1000)} KB` : "-"}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{t("Rows")}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {parsedCSV.totalRows} {t("rows")}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">{t("Columns")}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {parsedCSV.headers.length} {t("columns")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep3Map = () => {
    if (currentStep !== 3) return null;
    if (csvFields.length === 0 || !appInfo.connected) {
      return null;
    }
    // Show only mapping sections for the type selected in Step 1 (File info)
    // Validation banner is rendered once above step content (currentStep !== 2), not here
    return (
      <div className="space-y-4">
        <RuleFieldMapper
          sourcePlaceholder={t("CSV field")}
          showCompanyMapping={selectedEntityTypes.includes("COMPANY")}
          showDealMapping={selectedEntityTypes.includes("SALE")}
          showContactMapping={selectedEntityTypes.includes("CONTACT")}
          sourceOptions={{
            SALE: csvFields,
            CONTACT: csvFields,
            COMPANY: csvFields,
          }}
          fieldMappings={mappings}
          onChangeMappings={(newMappings) => {
            setMappings(newMappings);
          }}
          csvRows={csvRows}
          unSupportedFields={[
            "id",
            "start",
            "finish",
            "openedAt",
            "firstEmail",
            "lastEmail",
            "firstComment",
            "lastComment",
            "firstMeeting",
            "lastMeeting",
            "firstMeetingInPerson",
            "lastMeetingInPerson",
            "firstCall",
            "lastCall",
            "firstTask",
            "lastTask",
            "firstDeadline",
            "lastDeadline",
            "firstActivity",
            "lastActivity",
          ]}
        />
      </div>
    );
  };

  const renderStep4Review = () => {
    if (currentStep !== 4) return null;
    if (csvRows.length === 0 || Object.keys(mappings).length === 0) {
      // Show different message for draft items that need re-upload
      const isDraftWithoutData =
        currentImport.status === "draft" && csvRows.length === 0 && !parsedCSV;

      return (
        <div className="text-center py-12 space-y-4">
          <div className="text-gray-500">
            {isDraftWithoutData
              ? t("Cannot load CSV data for this draft. Please re-upload the file in Step 1.")
              : t("No data to review. Please complete mapping first.")}
          </div>
          {isDraftWithoutData && (
            <button
              onClick={() => setCurrentStep(2)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t("Go to Upload CSV")}
            </button>
          )}
        </div>
      );
    }

    const contactMappings = mappings["CONTACT"] || [];
    const companyMappings = mappings["COMPANY"] || [];
    const saleMappings = mappings["SALE"] || [];

    // Only show review blocks for the types selected in Step 1 (File info)
    const showContact = selectedEntityTypes.includes("CONTACT") && contactMappings.length > 0;
    const showAccount = selectedEntityTypes.includes("COMPANY") && companyMappings.length > 0;
    const showDeal = selectedEntityTypes.includes("SALE") && saleMappings.length > 0;

    return (
      <div className="space-y-6">
        {showContact && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Contacts")}</h3>
            <ReviewValues
              csvRows={csvRows}
              mappings={mappings}
              validationErrors={validationErrors}
              entityType="CONTACT"
            />
          </div>
        )}
        {showAccount && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Accounts")}</h3>
            <ReviewValues
              csvRows={csvRows}
              mappings={mappings}
              validationErrors={validationErrors}
              entityType="COMPANY"
            />
          </div>
        )}
        {showDeal && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("Deals")}</h3>
            <ReviewValues
              csvRows={csvRows}
              mappings={mappings}
              validationErrors={validationErrors}
              entityType="SALE"
            />
          </div>
        )}
      </div>
    );
  };

  const renderStep5Preview = () => {
    if (currentStep !== 5) return null;

    // Only show preview for the types selected in Step 1 (File info)
    const showContact = selectedEntityTypes.includes("CONTACT") && previewContacts.length > 0;
    const showAccount = selectedEntityTypes.includes("COMPANY") && previewCompanies.length > 0;
    const showDeal = selectedEntityTypes.includes("SALE") && previewDeals.length > 0;

    const hasPreviewData = showContact || showAccount || showDeal;
    if (!hasPreviewData) {
      return (
        <div className="text-center py-12 text-gray-500">
          {t("No preview data available. Please complete mapping first.")}
        </div>
      );
    }

    const duplicateContactsCount = previewContacts.filter((r) => r.status === "duplicate").length;
    const duplicateCompaniesCount = previewCompanies.filter((r) => r.status === "duplicate").length;
    const duplicateDealsCount = previewDeals.filter((r) => r.status === "duplicate").length;
    const showPreviewTabs = (showContact && (showAccount || showDeal)) || (showAccount && showDeal);

    return (
      <div className="space-y-6">
        {showPreviewTabs && (
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {showContact && (
                <button
                  onClick={() => setActivePreviewTab("contacts")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activePreviewTab === "contacts"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t("Contacts")} ({previewContacts.length})
                </button>
              )}
              {showAccount && (
                <button
                  onClick={() => setActivePreviewTab("companies")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activePreviewTab === "companies"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t("Accounts")} ({previewCompanies.length})
                </button>
              )}
              {showDeal && (
                <button
                  onClick={() => setActivePreviewTab("deals")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activePreviewTab === "deals"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t("Deals")} ({previewDeals.length})
                </button>
              )}
            </nav>
          </div>
        )}

        {activePreviewTab === "contacts" && showContact && duplicateContactsCount > 0 && (
          <DuplicateWarning
            duplicateCount={duplicateContactsCount}
            entityType="CONTACT"
            strategy={duplicateStrategy}
            onStrategyChange={setDuplicateStrategy}
          />
        )}

        {activePreviewTab === "companies" && showAccount && duplicateCompaniesCount > 0 && (
          <DuplicateWarning
            duplicateCount={duplicateCompaniesCount}
            entityType="COMPANY"
            strategy={duplicateStrategy}
            onStrategyChange={setDuplicateStrategy}
          />
        )}

        {activePreviewTab === "deals" && showDeal && duplicateDealsCount > 0 && (
          <DuplicateWarning
            duplicateCount={duplicateDealsCount}
            entityType="SALE"
            strategy={duplicateStrategy}
            onStrategyChange={setDuplicateStrategy}
          />
        )}

        {activePreviewTab === "contacts" && showContact ? (
          <PreviewTable records={previewContacts} entityType="CONTACT" mappings={mappings} />
        ) : activePreviewTab === "companies" && showAccount ? (
          <PreviewTable records={previewCompanies} entityType="COMPANY" mappings={mappings} />
        ) : activePreviewTab === "deals" && showDeal ? (
          <PreviewTable records={previewDeals} entityType="SALE" mappings={mappings} />
        ) : null}
      </div>
    );
  };

  // --- Render: import list (cards, actions, filters) ---
  const renderImportItemActions = (item: any) => {
    const showDel = item.status === "draft";
    const isDeleting = Boolean(deletingIds[item.importId]);

    const handleView = () => {
      setCurrentImport(item);
      if (item.objectTypes != null) {
        setSelectedEntityTypes(objectTypesToSelectedEntityTypes(item.objectTypes));
      }
      if (item.mappings && typeof item.mappings === "object") {
        setMappings(item.mappings);
      }
      if (item.status === "draft") {
        setShowUpload(true);
        setCurrentStep(3);
      } else if (item.status === "done") {
        setShowUpload(true);
        setCurrentStep(5);
      }
    };

    const handleRemove = () => {
      setDeleteConfirm({ show: true, importId: item.importId });
    };

    return (
      <Menu as="div" className="relative" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <MenuButton
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          aria-label={t("Actions")}
        >
          <MoreVertical className="h-5 w-5" aria-hidden />
        </MenuButton>
        <MenuItems
          anchor="bottom end"
          className="z-20 mt-1 w-40 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none"
        >
          <MenuItem>
            <button
              type="button"
              onClick={handleView}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 data-focus:bg-gray-50"
            >
              {item.status === "done" ? t("Preview Import") : t("View")}
            </button>
          </MenuItem>
          {showDel && (
            <MenuItem>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleRemove}
                className={`flex w-full items-center px-3 py-2 text-left text-sm data-focus:bg-gray-50 ${
                  isDeleting
                    ? "cursor-not-allowed text-gray-400"
                    : "text-red-600 hover:bg-red-50 data-focus:bg-red-50"
                }`}
              >
                {isDeleting ? t("Removing...") : t("Remove")}
              </button>
            </MenuItem>
          )}
        </MenuItems>
      </Menu>
    );
  };

  const getStatusBadgeStyle = (status: string) => {
    if (status === "done") return "bg-[#dbeafe] text-[#1e40af]";
    if (status === "failed") return "bg-red-100 text-red-800";
    if (status === "processing") return "bg-amber-100 text-amber-800";
    return "bg-[#f3f4f6] text-[#1f2937]";
  };

  const OBJECT_TYPE_BADGE_CONFIG: Record<
    string,
    { labelKey: string; Icon: React.ComponentType<{ className?: string }>; iconClass: string }
  > = {
    SALE: { labelKey: "Deals", Icon: DollarSign, iconClass: "text-amber-600" },
    COMPANY: { labelKey: "Companies", Icon: Building2, iconClass: "text-blue-600" },
    CONTACT: { labelKey: "Contacts", Icon: Users, iconClass: "text-indigo-600" },
    CONTACT_COMPANY: { labelKey: "Contacts & Accounts", Icon: Users, iconClass: "text-blue-600" },
  };

  const getObjectTypeBadge = (
    item: any
  ): {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    pillClass: string;
    iconClass: string;
  } | null => {
    const kind = getObjectTypeKindFromItem(item);
    if (kind == null) return null;
    const config = OBJECT_TYPE_BADGE_CONFIG[kind];
    if (!config) return null;
    return {
      label: t(config.labelKey),
      Icon: config.Icon,
      pillClass: OBJECT_TYPE_BADGE_PILL,
      iconClass: config.iconClass,
    };
  };

  const renderImportCard = (item: any) => {
    const createdAgo = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
    const descParts = [
      item.createdBy?.displayName ? `${item.createdBy.displayName} · ${createdAgo}` : createdAgo,
      `${item.rowNumber} ${t("rows")}`,
      `${item.columnNumber} ${t("columns")}`,
      `${Math.round(item.fileSize / 1000)} KB`,
    ].filter(Boolean);
    const description = descParts.join(" • ");

    const openDetail = () => {
      setCurrentImport(item);
      if (item.objectTypes != null) {
        setSelectedEntityTypes(objectTypesToSelectedEntityTypes(item.objectTypes));
      }
      if (item.mappings && typeof item.mappings === "object") {
        setMappings(item.mappings);
      }
      if (item.status === "draft") {
        setShowUpload(true);
        setCurrentStep(3);
      } else if (item.status === "done") {
        setShowUpload(true);
        setCurrentStep(5);
      }
    };

    return (
      <div
        key={item.importId}
        role="button"
        tabIndex={0}
        className="flex min-h-[56px] cursor-pointer items-center justify-between gap-4 rounded-[12px] border border-[#e5e7eb] bg-white pl-2 pr-4 py-2 transition-colors hover:bg-gray-50"
        onClick={openDetail}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openDetail())
        }
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f3f4f6]">
            <FileSpreadsheet className="h-6 w-6 text-gray-500" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="truncate text-[14px] font-semibold leading-5 text-[#1f2937]"
                title={item.fileName}
              >
                {item.fileName}
              </span>
              {(() => {
                const badge = getObjectTypeBadge(item);
                if (!badge) return null;
                const { label, Icon, pillClass, iconClass } = badge;
                return (
                  <span
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium ${pillClass}`}
                  >
                    <Icon className={`h-3 w-3 shrink-0 ${iconClass}`} aria-hidden />
                    {label}
                  </span>
                );
              })()}
            </div>
            <div className="text-[12px] leading-4 text-[#6b7280]">{description}</div>
            {(item.status === "done" && item.statusMessage) ||
            (item.status === "failed" && item.detailedErrors) ? (
              <div className="mt-1 text-xs leading-4 text-gray-600 whitespace-pre-line">
                {item.status === "done" ? item.statusMessage : item.detailedErrors}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span
            className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium leading-4 ${getStatusBadgeStyle(
              item.status
            )}`}
          >
            {item.status === "processing" && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            )}
            {item.status === "done"
              ? t("Done")
              : item.status === "failed"
                ? t("Failed")
                : item.status === "processing"
                  ? t("Processing")
                  : t("Draft")}
          </span>
          {renderImportItemActions(item)}
        </div>
      </div>
    );
  };

  const filteredImports = useMemo(() => {
    let list = imports;
    if (importSearchQuery.trim()) {
      const q = importSearchQuery.trim().toLowerCase();
      list = list.filter((item: any) => (item.fileName || "").toLowerCase().includes(q));
    }
    if (importStatusFilter) {
      list = list.filter((item: any) => item.status === importStatusFilter);
    }
    return list;
  }, [imports, importSearchQuery, importStatusFilter]);

  const renderImportList = () => (
    <ImportListSection
      visible={!showUpload && !!appInfo.connected}
      loading={importsListLoading}
      filteredImports={filteredImports}
      importsCount={imports.length}
      searchQuery={importSearchQuery}
      onSearchChange={setImportSearchQuery}
      statusFilter={importStatusFilter}
      onStatusFilterChange={setImportStatusFilter}
      onNewImportClick={handleShowNewImport}
      renderCard={renderImportCard}
    />
  );

  // --- Confirmation message (for import dialog) ---
  // Count by status only for records matching current import type (Deal | Contact | Account)
  const countByStatus = (status: string) => {
    let count = 0;
    if (selectedEntityTypes.includes("CONTACT"))
      count += previewContacts.filter((r) => r.status === status).length;
    if (selectedEntityTypes.includes("COMPANY"))
      count += previewCompanies.filter((r) => r.status === status).length;
    if (selectedEntityTypes.includes("SALE"))
      count += previewDeals.filter((r) => r.status === status).length;
    return count;
  };

  const importConfirmationMessage = () => {
    let totalRecords = 0;
    if (selectedEntityTypes.includes("CONTACT")) totalRecords += previewContacts.length;
    if (selectedEntityTypes.includes("COMPANY")) totalRecords += previewCompanies.length;
    if (selectedEntityTypes.includes("SALE")) totalRecords += previewDeals.length;
    const newRecords = countByStatus("new");
    const updateRecords = countByStatus("update");
    const duplicateRecords = countByStatus("duplicate");

    const details: string[] = [];
    if (newRecords > 0) details.push(`• ${newRecords} ${t("new records")}`);
    if (updateRecords > 0) details.push(`• ${updateRecords} ${t("records will be updated")}`);
    if (duplicateRecords > 0) {
      const action = duplicateStrategy === "update" ? t("updated") : t("skipped");
      details.push(`• ${duplicateRecords} ${t("duplicate records")} (${t("will be")} ${action})`);
    }

    const question = `${t("Are you sure you want to import")} ${totalRecords} ${t("records")}?`;
    return details.length > 0 ? `${question}\n\n${details.join("\n")}` : question;
  };

  // --- Render: main content ---
  const renderContent = () => {
    if (activeTab == "SETTING") {
      return (
        <>
          {renderImportList()}

          {showUpload && appInfo.connected && (
            <div
              className={`relative ${currentStep >= 1 && currentStep <= 5 ? "pb-24" : ""}`}
              data-import-setting-steps
            >
              {loadingImportDetail && (
                <div className="absolute inset-0 z-10 flex min-h-[200px] items-center justify-center gap-2 rounded-lg bg-white/90 text-[14px] text-[#6b7280]">
                  <ChartLoading height={200} label={t("Loading...")} />
                </div>
              )}
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-4 flex-wrap pt-3">
                  {renderStepNavigation()}
                </div>
                {currentStep !== 2 && renderValidationBanner()}
                {renderStep1FileInfo()}
                {renderStep2Upload()}
                {renderStep3Map()}
                {renderStep4Review()}
                {renderStep5Preview()}
              </div>

              {/* Footer for steps 1–4 */}
              {currentStep >= 1 && currentStep <= 5 && (
                <div
                  className="fixed bottom-0 left-0 lg:left-[59px] right-0 z-20 flex justify-center pointer-events-none"
                  aria-hidden
                >
                  <div className="w-full max-w-full pointer-events-auto bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] px-4 py-3 sm:px-6 pr-[104px] sm:pr-[104px]">
                    <div className="flex items-center justify-between gap-3 w-full">
                      <button
                        type="button"
                        onClick={handleResetImport}
                        className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                      >
                        {t("Cancel")}
                      </button>
                      <div className="flex items-center gap-3">
                        {currentStep > 1 && currentImport.status !== "done" && (
                          <button
                            type="button"
                            onClick={handleBackStep}
                            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                          >
                            {t("Back")}
                          </button>
                        )}
                        {currentStep === 5 && currentImport.status !== "done" ? (
                          <button
                            type="button"
                            onClick={handleStartImportClick}
                            disabled={starting}
                            className={`px-5 py-2.5 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-600 ${
                              starting
                                ? "bg-blue-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-500"
                            }`}
                          >
                            {starting ? t("Starting...") : t("Start Import")}
                          </button>
                        ) : currentStep !== 5 ? (
                          <button
                            type="button"
                            onClick={handleNextStep}
                            disabled={currentStep === 1 && selectedEntityTypes.length === 0}
                            className={`px-5 py-2.5 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                              currentStep === 1 && selectedEntityTypes.length === 0
                                ? "bg-blue-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-500"
                            }`}
                          >
                            {t("Next")}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <ConfirmDialog
            visible={showImportConfirmation}
            description={importConfirmationMessage()}
            title={t("Importing")}
            closeBtnLabel={t("Close")}
            submitBtnLabel={t("Confirm")}
            submitting={starting}
            onClose={() => {
              setShowImportConfirmation(false);
            }}
            onSubmit={handleStartImport}
          />
          <ConfirmDialog
            visible={showImportResult}
            description={
              importResult ? (
                <div className="space-y-3">
                  {importResult.message && (
                    <div className="text-sm text-gray-700 whitespace-pre-line">
                      {importResult.message}
                    </div>
                  )}
                  <div className="space-y-2 text-sm">
                    {importResult.successCount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t("Successful records")}:</span>
                        <span className="font-medium text-green-600">
                          {importResult.successCount}
                        </span>
                      </div>
                    )}
                    {importResult.failCount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t("Failed records")}:</span>
                        <span className="font-medium text-red-600">{importResult.failCount}</span>
                      </div>
                    )}
                  </div>
                  {importResult.detailedErrors && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {t("Error details")}:
                      </div>
                      <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-md p-3">
                        <div className="text-xs text-red-600 whitespace-pre-line font-mono">
                          {importResult.detailedErrors}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                ""
              )
            }
            title={t("Import Result")}
            closeBtnLabel={t("Close")}
            submitBtnLabel={t("OK")}
            submitting={false}
            onClose={handleCloseImportResult}
            onSubmit={handleCloseImportResult}
          />
          <ConfirmDialog
            visible={deleteConfirm.show}
            title={t("Remove import")}
            description={t("Are you sure you want to remove this draft import?")}
            dangerBtn
            closeBtnLabel={t("Cancel")}
            submitBtnLabel={t("Remove")}
            onClose={() => setDeleteConfirm({ show: false })}
            onSubmit={() =>
              deleteConfirm.importId
                ? handleDelete(deleteConfirm.importId).then(() => setDeleteConfirm({ show: false }))
                : Promise.resolve()
            }
          />
        </>
      );
    }
    return (
      <>
        <RuleForm projectId={appInfo.projectId} ruleId={ruleId} />
      </>
    );
  };

  return (
    <>
      <Tab items={Object.values(ImportSettingTabs)} value={activeTab} onTabChange={setActiveTab} />
      {renderContent()}
    </>
  );
};

export default ImportSetting;
